import { GraphMindClient, GraphMindApiError, readSseStream, type StreamEvent } from "@graphmind/api-client";
import { QueryClient } from "@tanstack/react-query";

import { ensureFreshToken, forceRefresh } from "@/lib/auth-token";

// Re-exported so callers can branch on HTTP status (e.g. retry policy, treating
// a 404 on delete as already-gone) without importing the client package directly.
export { GraphMindApiError };

// Same-origin: vite proxies /api in dev; prod serves behind the API host, so the
// `graphmind_id_token` cookie rides along automatically. (A cross-host client
// would need `credentials: "include"` — keep these same-origin or the cookie,
// and thus auth, silently stops being sent.)
//
// `beforeRequest` keeps the auth cookie fresh before every call; `onUnauthorized`
// recovers a stale-cookie 401 by refreshing the token and signalling a retry.
export const api = new GraphMindClient({
  baseUrl: "",
  beforeRequest: ensureFreshToken,
  onUnauthorized: forceRefresh,
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/** POST an AI request and iterate SSE events, abortable mid-stream. */
export async function* aiStream(
  path: "/api/ai/explain" | "/api/ai/rag",
  body: unknown,
  signal: AbortSignal,
): AsyncGenerator<StreamEvent> {
  const send = async () => {
    await ensureFreshToken();
    return fetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify(body),
      signal,
    });
  };
  // Auth is checked once before the stream starts, so a 401/403 here precedes
  // any event — refresh and re-issue once before reading.
  let resp = await send();
  if ((resp.status === 401 || resp.status === 403) && (await forceRefresh())) {
    resp = await send();
  }
  yield* readSseStream(resp);
}

/** Best-effort persist that survives page unload. A normal async request is cut
 * off when the tab is closing/refreshing, so the editor's autosave routes its
 * final flush through here: `keepalive` lets the browser finish the PATCH after
 * the page is gone. Same-origin cookies carry auth, matching {@link api}. */
export function flushNodeText(nodeId: string, text: string): void {
  void fetch(`/api/nodes/${nodeId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
    keepalive: true,
  }).catch(() => {
    /* unload path — nothing we can surface */
  });
}

/** Multipart upload (the typed client is JSON-only). */
export async function uploadDocument(
  file: File,
  /** Path relative to a chosen folder (folder import), e.g. "Refs/2024/x.pdf".
   * The server mirrors its folders under Papers/. */
  relPath?: string,
): Promise<{ document: DocumentOut; job_id: string | null; deduplicated: boolean }> {
  const form = new FormData();
  form.append("file", file);
  if (relPath) form.append("rel_path", relPath);
  const send = async () => {
    await ensureFreshToken();
    return fetch("/api/documents/upload", { method: "POST", body: form });
  };
  let resp = await send();
  if ((resp.status === 401 || resp.status === 403) && (await forceRefresh())) {
    resp = await send();
  }
  if (!resp.ok) throw new Error(`upload failed: ${resp.status} ${await resp.text()}`);
  return resp.json();
}

// ---- Wire types (mirror server/src/graphmind/domain; schema.d.ts carries the
// full OpenAPI shapes — these are the narrowed runtime shapes the UI uses). ----

export type NodeType =
  | "document"
  | "section"
  | "fragment"
  | "concept"
  | "note"
  | "ai_output"
  | "org";

export interface NodeOut {
  id: string;
  type: NodeType;
  subtype: string | null;
  label: string;
  /** display title (Node.meta['title']); distinct from label/filename, null/absent until set */
  title?: string | null;
  summary: string | null;
  keywords: string[];
  document_id: string | null;
  anchor: Record<string, unknown> | null;
  origin: "parser" | "llm" | "user" | "system";
  status: "suggested" | "confirmed";
  cluster_id: string | null;
  created_at: string | null;
  /** editable markdown body (notes); null for nodes without one */
  text?: string | null;
}

export interface EdgeOut {
  id: string;
  src_id: string;
  dst_id: string;
  type: string;
  origin: string;
  confidence: number | null;
  status: "suggested" | "confirmed";
  evidence: Record<string, unknown> | null;
  /** human-readable, user-editable descriptor of the relationship */
  label: string | null;
}

/** One row of the edge matrix, as the server serves it.
 *
 * The studio does NOT mirror `graphs/ontology.py`. A copy of the matrix in
 * TypeScript would rot the first time ALLOWED changed, and the relationship
 * picker would start offering an edge the write chokepoint rejects. So the
 * matrix is fetched: `pairs` is the raw allowed list ("*" means any node type)
 * and `authorable` marks what a PERSON may draw, as against what only the
 * parser or the pipeline may assert. */
export interface EdgeTypeRule {
  type: string;
  /** How it reads in "<source> ___ <target>" — "depends on", not "depends_on". */
  phrase: string;
  authorable: boolean;
  pairs: [string, string][];
}

export interface GraphOntology {
  edge_types: EdgeTypeRule[];
}

export interface ClusterOut {
  id: string;
  label: string;
  summary: string | null;
  size: number;
}

export interface GraphOverview {
  node_counts: Record<string, number>;
  edge_counts: Record<string, number>;
  document_nodes: NodeOut[];
  concept_nodes: NodeOut[];
  org_nodes: NodeOut[];
  /** Standalone notes + saved AI outputs — rendered on the atlas like any
   * other node type (see palette.ts's NODE_COLOR/NODE_RADIUS `note`/`ai_output`
   * entries). */
  annotation_nodes: NodeOut[];
  edges: EdgeOut[];
  clusters: ClusterOut[];
}

// ---- Orbit graphing system: a node sits on a circular track and may only move
// ALONG it. The server stores the angle (theta) and derives x/y on the ring. ----
export type OrbitRole = "ring" | "anchor" | "free" | "vertex";
export type OrbitKind = "satellite" | "planetary" | "polygon" | "free";

export interface OrbitNodeOut {
  node_id: string;
  label: string;
  type: NodeType;
  role: OrbitRole;
  theta: number;
  x: number;
  y: number;
}

export interface OrbitOut {
  id: string;
  kind: OrbitKind;
  label: string | null;
  center_node_id: string | null;
  center_label: string | null;
  center_type: NodeType | null;
  center_x: number;
  center_y: number;
  radius: number;
  belt_width: number;
  angle_offset: number;
  nodes: OrbitNodeOut[];
  created_at: string | null;
}

export interface OrbitEdgeOut {
  id: string;
  src_id: string;
  dst_id: string;
  kind: string;
}

export interface OrbitLayout {
  orbits: OrbitOut[];
  edges: OrbitEdgeOut[];
  dirty: boolean;
}

/** @deprecated The orbit endpoint now returns {@link OrbitLayout}. */
export type OrbitOverview = OrbitLayout;

// ---- Classic writing engine: per-stroke markup rows (page-relative [0,1]
// geometry) spoken over /api/markup/* — the store the Fabric-based editor
// writes. Coexists with the Excalidraw drawing layer below behind the user's
// writing-engine toggle. ----

/** Mirrors the server's `AnnotationType` (server/.../domain/annotations.py) —
 * KEEP IN SYNC. The ten drawn shapes need no new fields: a box shape reuses
 * `rect`, a line/arrow is two `points`. */
export type AnnotationType =
  | "ink"
  | "text"
  | "highlight"
  | "note"
  | "rect"
  | "ellipse"
  | "diamond"
  | "line"
  | "arrow"
  | "parallelogram"
  | "terminator"
  | "hexagon"
  | "cylinder"
  | "fill"
  | "table";

export interface AnnotationPoint {
  x: number;
  y: number;
  pressure?: number | null;
}

export interface Annotation {
  id: string;
  document_id: string;
  created_by: string;
  page: number;
  type: AnnotationType;
  /** ink: page-relative [0,1] polyline. line/arrow: exactly two endpoints
   * (tail, tip) — not a box, so a segment drawn in any direction round-trips. */
  points?: AnnotationPoint[] | null;
  text?: string | null;
  color?: string | null;
  font_family?: string | null;
  /** text height as a fraction of page height */
  font_size_rel?: number | null;
  /** ink width as a fraction of page width */
  stroke_rel?: number | null;
  /** 0..1 opacity (highlighter / translucent ink) */
  opacity?: number | null;
  /** text/highlight box, and a rect/ellipse/diamond's bounding box —
   * page-relative [0,1] */
  rect?: { x: number; y: number; w: number; h: number } | null;
  /** fill: the traced polygon rings, page-relative [0,1]. Ring 0 is the outer
   * boundary and the rest are holes; every renderer fills them EVEN-ODD. */
  rings?: AnnotationPoint[][] | null;
  /** table: column widths and row heights as fractions of its own box. */
  cols?: number[] | null;
  rows?: number[] | null;
  /** shape styling — all optional; absent means the outline-only solid figure. */
  fill_color?: string | null;
  dash?: boolean | null;
  /** rect corner rounding, as a fraction of the box's SHORT side */
  corner_radius?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface MarkupConfig {
  enabled: boolean;
  fonts: string[];
}

// ---- Drawing layer: one Excalidraw scene per document. Elements are
// Excalidraw's own shape (the studio never re-declares it — the editor owns the
// format); the server validates the envelope only. Image BYTES are externalized
// by fileId and travel via the drawing/files endpoints. ----

export interface DrawingScene {
  schema_version: number;
  elements: Record<string, unknown>[];
  /** Allowlisted persisted subset: viewBackgroundColor, gridSize, gm_pages. */
  appState: Record<string, unknown>;
  file_ids: string[];
}

export interface DrawingOut {
  document_id: string;
  scene: DrawingScene;
  updated_at: string | null;
}

export interface DrawingConfig {
  enabled: boolean;
}

/** One externalized Excalidraw BinaryFile (the id is Excalidraw's content
 * hash, carried in the URL — PUTs are idempotent). */
export interface DrawingFilePayload {
  mimeType: string;
  dataURL: string;
}

export interface VaultEntry {
  name: string;
  relpath: string;
  dir: boolean;
  children?: VaultEntry[];
  ref?: {
    kind: "document" | "node";
    id: string;
    node_type?: NodeType;
    document_id?: string | null;
  };
}

export interface VaultTree {
  enabled: boolean;
  root: string | null;
  tree: VaultEntry[];
}

// ── the four-category product taxonomy (GET /api/folders, GET /api/notes) ────
// Mirrors `server/src/graphmind/domain/vault.py` + `domain/notes.py`. The DISK
// only has Papers/ + Markdown/; these four are derived from `source_type`, and
// are what the studio shows. See
// docs/architecture/backend/data-model-and-storage.md, the folder model.

export type NoteCategory = "markdown" | "ingested" | "handwritten" | "website";

export const NOTE_CATEGORIES: NoteCategory[] = [
  "markdown",
  "ingested",
  "handwritten",
  "website",
];

export const CATEGORY_LABEL: Record<NoteCategory, string> = {
  markdown: "Markdown",
  ingested: "Ingested",
  handwritten: "Handwritten",
  website: "Website",
};

/** A folder colour is a palette TOKEN, never free hex — see `lib/palette.ts`. */
export type FolderColor = string;

/** A file sitting directly in a folder — the leaf rows the explorer draws.
 * Deliberately thin; previews/descriptions/tags live on the card feed. */
export interface FolderItemOut {
  id: string;
  kind: "document" | "node";
  title: string;
  category: NoteCategory;
  /** The GRAPH node this row represents — itself for a note, its DOCUMENT
   * node for a document. What lets selecting a file drive the inspector. */
  node_id?: string | null;
  node_type?: NodeType | null;
  status?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface FolderOut {
  /** null for a DERIVED folder: it exists because items say they live there,
   * but has no row yet, so it cannot carry a colour or be renamed until one is
   * created (POST /api/folders is idempotent and does exactly that). */
  id: string | null;
  category: NoteCategory;
  /** "/"-joined, relative to the category. "" is the category root. */
  path: string;
  name: string;
  color: FolderColor | null;
  item_count: number;
  total_count: number;
  /** Files directly in this folder. Populated only when the tree was fetched
   * with `?items=true` (the explorer); empty for the filter popover. */
  items: FolderItemOut[];
  children: FolderOut[];
}

/** Batch move — see `domain/moves.py`. A category is intrinsic and a folder is
 * chosen, so items of the wrong category come back in `rejected` with a reason
 * rather than failing the whole batch. */
export interface MoveItemRef {
  kind: "document" | "node";
  id: string;
}

export interface VaultMoveResult {
  moved: string[];
  rejected: { id: string; reason: string }[];
  /** Null when the destination was a shared folder (each item kept its own). */
  category: NoteCategory | null;
  folder: string;
}

/** Duplicating notes. `created` are the new ids; anything that cannot honestly
 * be duplicated (an ingested file is the same bytes) comes back with a reason. */
export interface CopyResult {
  created: string[];
  rejected: { id: string; reason: string }[];
}

/** A folder seen ACROSS categories — "Research", holding its markdown notes and
 * its handwriting and its papers. Purely derived: the per-category folders are
 * its projections, which is why they hold the same files rather than copies. */
export interface SharedFolderOut {
  path: string;
  name: string;
  categories: NoteCategory[];
  color: FolderColor | null;
  total_count: number;
  /** The per-category projections; each holds the real items and subfolders. */
  facets: FolderOut[];
  children: SharedFolderOut[];
}

export interface FolderTree {
  /** Always all four, even when empty. */
  categories: FolderOut[];
  /** The same folders, gathered across categories. */
  shared: SharedFolderOut[];
  colors: FolderColor[];
}

/** The document's own paper — see `domain/notes.py::NoteSurface`. Present for
 * anything that stands for a PAGE; absent for text-bodied notes. `paper`,
 * `layout` and `spacing` are the RAW stored canvas values: the defaulting rules
 * live in `components/reader/paper.ts` and are applied there, not twice. */
export interface NoteSurfaceOut {
  background: string;
  paper?: string | null;
  layout?: string | null;
  spacing?: number | null;
}

export interface NotePreviewOut {
  mode: "text" | "image" | "card";
  text?: string | null;
  url?: string | null;
  surface?: NoteSurfaceOut | null;
}

export interface NoteCardOut {
  id: string;
  kind: "document" | "node";
  title: string;
  category: NoteCategory;
  /** Segments BELOW the category: `["Research", "Interpretability"]` renders as
   * `Markdown / Research / Interpretability`. */
  folder_path: string[];
  /** Colour token per `folder_path` segment, parallel to it. */
  folder_colors: (FolderColor | null)[];
  description: string | null;
  tags: string[];
  preview: NotePreviewOut;
  created_at: string | null;
  updated_at: string | null;
  node_type: NodeType | null;
  document_id: string | null;
  status: string | null;
  mime: string | null;
}

export interface NotesPage {
  items: NoteCardOut[];
  next_cursor: string | null;
}

export type NoteSort = "recent" | "alpha" | "relevance";

/** An item's description + tags, as STORED — see `domain/metadata.py`. The
 * server returns the normalised form ("Sparse Autoencoders" → "sparse-
 * autoencoders"), so the panel echoes what search and the cards will use rather
 * than what was typed. */
export interface MetadataOut {
  id: string;
  kind: "document" | "node";
  description: string | null;
  tags: string[];
  enriching: boolean;
}

export interface Neighborhood {
  center: NodeOut;
  nodes: NodeOut[];
  edges: EdgeOut[];
}

export interface DocumentOut {
  id: string;
  title: string;
  source_type: string;
  source_ref: string;
  content_sha256: string;
  // "empty" = parsed without error but produced nothing indexable (a scanned
  // PDF with no text layer). Distinct from "failed", which means the parser
  // raised — conflating them makes a real parser bug look like an unlucky file.
  status: "registered" | "processing" | "ready" | "failed" | "empty";
  stage_status: Record<string, string>;
  /**
   * The ONE answer to "is this still processing?", derived server-side from
   * `status` and `stage_status` together.
   *
   * Read this. Do NOT derive your own from the two fields above: they are
   * written by different code with no invariant between them, so they disagree
   * — in one live library 39 of 51 documents said `ready` with every stage
   * still `pending`. Each surface that reconciled them itself reached a
   * different answer, which is how a document came to look stuck forever.
   */
  processing:
    | "queued"
    | "working"
    | "ready"
    | "partial"
    | "failed"
    | "not_applicable";
  error: string | null;
  ingest_cost_usd: number;
  page_count: number | null;
  size_bytes: number | null;
  mime: string | null;
  /** Scraped page metadata for a web-registered document. NOTE this
   * over-specifies the wire: the server declares `dict[str, str] | None`, so
   * any of these nine keys can be ABSENT on a page that did not carry it. The
   * narrowing to an optional record is a behavior-visible change and is not
   * made here. */
  web: {
    title: string;
    url: string;
    canonical_url: string;
    description: string;
    author: string;
    site_name: string;
    published_at: string;
    image_url: string;
    language: string;
  } | null;
  has_pdf_snapshot: boolean;
  /** Blank-canvas notes: the surface's background color (hex). Classic engine
   * paints it; Excalidraw seeds viewBackgroundColor from it. */
  canvas_background: string | null;
  /** Blank-canvas notes: which writing engine OWNS this note — "excalidraw" or
   * "legacy". Null for notes created before the toggle ⇒ treated as legacy
   * (that engine wrote them). */
  canvas_engine?: string | null;
  /** Classic-engine canvases: "pages" (US-Letter stack) or "infinite" (a single
   * camera surface). Null ⇒ treated as pages. */
  canvas_layout?: string | null;
  /** Classic-engine canvases: the paper style behind the ink — "blank" |
   * "ruled" | "grid" | "dots". Null ⇒ blank. */
  canvas_paper?: string | null;
  /** Paper cell spacing as a MULTIPLE of the surface's natural unit, so the
   * setting is resolution-independent (same note, phone/tablet/print). */
  canvas_paper_spacing?: number | null;
  /** The latest handwriting/text transcript from the annotation-OCR sweep (null
   * until this doc's ink has been transcribed once), and when it was produced.
   * Surfaced for the reader's Text tab; refreshes when the document query refetches. */
  annotation_ocr?: string | null;
  annotation_ocr_at?: string | null;
  /** Atlas visibility. Private by default (owner-only); "public" opts this one
   * document into every atlas surface — manifest, content, search, and the
   * hosted site's chat RAG. Server-enforced; a scoped atlas PAT can't flip it. */
  visibility: "public" | "private";
  created_at: string | null;
  /** Site/authoring metadata (atlas-site-api S2), set only by
   * `PATCH /documents/{id}/meta` and never inferred from content. Absent from
   * the studio's surfaces today; declared so a reader is not misled into
   * thinking a document row carries none. */
  site_description?: string | null;
  site_tags?: string[];
  links?: Record<string, unknown>[];
  site?: Record<string, unknown>;
  /** Free-form key -> text attached to this document (captions, per-field
   * notes). */
  associated_text?: Record<string, string>;
  /** The vault-folder grouping key (`meta['folder']`, relative to Papers/, no
   * prefix) — the SAME field folder/file-type search scoping and CO_LOCATED
   * linking key off. Null means the Papers/ root. */
  folder?: string | null;
}

export interface ReaderSection {
  id: string;
  label: string;
  text: string;
  summary: string | null;
  anchor: Record<string, unknown> | null;
  order: number;
}

export interface ReaderPayload {
  document: DocumentOut;
  sections: ReaderSection[];
}

export interface SearchHit {
  node: NodeOut;
  score: number;
  source: string;
  /** The sub-node window that actually matched, when the hit was won by a chunk
   * vector rather than the node's whole-body one. Both are null for a purely
   * lexical hit and for a whole-node semantic win. This is the exact matched
   * passage — the part of a hit that is usable as evidence, since a "section"
   * can be a whole PDF page. Nothing in the studio surfaces it yet. */
  passage?: string | null;
  chunk_index?: number | null;
}

export interface JobOut {
  id: string;
  kind: string;
  status: "queued" | "running" | "done" | "failed";
  error: string | null;
  result: Record<string, unknown> | null;
  /** ISO timestamps the route has always returned (domain/jobs.py JobOut).
   * `finished_at` is null while the job is queued or running. */
  created_at?: string | null;
  finished_at?: string | null;
}

export interface UsageSummary {
  window_start: string;
  total_cost_usd: number;
  total_input_tokens: number;
  total_output_tokens: number;
  by_model: Record<string, number>;
  by_operation: Record<string, number>;
  request_count: number;
}

export interface WorkspaceItem {
  id: string;
  kind: "node" | "search" | "snippet" | "ai_output";
  title: string;
  ref_id: string | null;
  body: string | null;
  conversation_id?: string | null;
  project_id?: string | null;
  created_at: string | null;
}

export interface ConversationSummary {
  id: string;
  title: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface ProjectOut {
  id: string;
  name: string;
  created_at: string | null;
  items: WorkspaceItem[];
}

export interface DriveStatus {
  connected: boolean;
  provider: string;
  subject: string | null;
  scope: string;
}

// ---- Billing / entitlements (see server/src/graphmind/api/routes.py
// ~136-302) — two independent paid add-ons on one subscription: "storage"
// (unlimited storage) and "models" (frontier models). Both default to false
// (free tier) when billing is disabled for this deployment. ----

export type BillingItem = "storage" | "models";

export interface BillingStatus {
  /** Whether Stripe billing is configured for this deployment at all. When
   * false the studio must never offer an upgrade CTA — but it does NOT unlock
   * anything: `storage_unlimited`/`models_premium` still report the raw
   * derivation, and both are false without an active/trialing subscription or
   * a comp-PRO allowlist match (GRAPHMIND_PRO_EMAILS). See
   * services/adapters.py::_derive and gateway/plan.py::Entitlements. */
  enabled: boolean;
  storage_unlimited: boolean;
  models_premium: boolean;
  status: "none" | "active" | "trialing" | "past_due" | "canceled" | (string & {});
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  storage_price_usd: number;
  models_price_usd: number;
}

export interface AppConfig {
  version: string;
  min_client_version: string;
  require_auth: boolean;
  features: {
    annotation: boolean;
    accounts: boolean;
    drive: boolean;
    billing: boolean;
  };
  billing: {
    stripe_publishable_key: string | null;
    storage: { free_mb: number; unlimited_price_usd: number; available: boolean };
    models: { price_usd: number; available: boolean };
  };
  disclosure: {
    ai_processing: boolean;
    ai_provider: string;
    account_deletion: boolean;
    privacy_policy_url: string;
    terms_url: string;
    ai_disclosure_url: string;
    sensitive_info: string;
  };
}
